import { useState, useRef, useEffect } from "react";
import { Send, Loader2, MessageSquare } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

const fmtTime = (iso) => {
  try {
    return new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch { return ""; }
};

// Presentational chat thread. Parent owns messages + fetching/polling.
export function AppointmentChat({ messages = [], onSend, viewerRole, testidPrefix = "chat", labels }) {
  const { t } = useLang();
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages]);

  const meLabel = labels?.me || (viewerRole === "admin" ? t("admin.chat_you") : t("portal.chat_you"));
  const themLabel = labels?.them || (viewerRole === "admin" ? t("admin.chat_client") : t("portal.chat_garage"));
  const placeholder = labels?.placeholder || (viewerRole === "admin" ? t("admin.chat_ph") : t("portal.chat_ph"));
  const emptyText = labels?.empty || (viewerRole === "admin" ? t("admin.chat_empty") : t("portal.chat_empty"));

  const submit = async (e) => {
    e?.preventDefault?.();
    const v = text.trim();
    if (!v || sending) return;
    setSending(true);
    try {
      await onSend(v);
      setText("");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col" data-testid={`${testidPrefix}-panel`}>
      <div className="max-h-72 overflow-y-auto space-y-3 pr-1" data-testid={`${testidPrefix}-messages`}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center gap-2 text-zinc-500">
            <MessageSquare className="w-5 h-5 opacity-60" />
            <p className="text-xs font-mono-tech max-w-xs">{emptyText}</p>
          </div>
        ) : (
          messages.map((m) => {
            const mine = m.sender === viewerRole;
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`${testidPrefix}-msg-${m.sender}`}>
                <div className={`max-w-[80%] px-3.5 py-2.5 text-sm leading-relaxed border ${mine
                  ? "bg-[#E60000]/12 border-[#E60000]/40 text-white rounded-l-xl rounded-tr-xl"
                  : "bg-white/5 border-white/10 text-zinc-200 rounded-r-xl rounded-tl-xl"}`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`font-mono-tech text-[9px] uppercase tracking-[0.14em] ${mine ? "text-[#FF6B6B]" : "text-[#00E5FF]"}`}>{mine ? meLabel : themLabel}</span>
                    <span className="font-mono-tech text-[9px] text-zinc-500">{fmtTime(m.created_at)}</span>
                  </div>
                  <p className="whitespace-pre-wrap break-words">{m.text}</p>
                </div>
              </div>
            );
          })
        )}
        <div ref={endRef} />
      </div>

      <form onSubmit={submit} className="mt-3 flex items-center gap-2">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={placeholder}
          data-testid={`${testidPrefix}-input`}
          className="flex-1 h-11 bg-[#050505] border border-white/10 px-3 text-white text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#00E5FF] rounded-none"
        />
        <button
          type="submit"
          disabled={sending || !text.trim()}
          data-testid={`${testidPrefix}-send`}
          className="h-11 px-4 inline-flex items-center justify-center bg-[#E60000] text-white border border-[#E60000] hover:bg-transparent hover:text-[#E60000] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </form>
    </div>
  );
}
